import React, { useMemo, useState } from 'react';
import { Event } from '@go-shared/types/event';
import {
  timeToString,
  addMinutesToTime,
  timeToMinutes,
  DEFAULT_TIMESTR_FORMAT,
} from '@go-shared/lib/time';
import { Time } from '@go-shared/types/time';
import { GroupEvent } from '@go-shared/types/groupEvent';
import { BubbleBadge, BubbleButton, BubbleInput, BubbleTable } from './BubbleUI';
import { InlineIcon } from '@spark-labs/avocado-ui';

import './EventListPanel.scss';
import { BubbleTableFields, BubbleTableTemplate } from './BubbleUI/BubbleTable';
import { notUndefined } from '@go-shared/lib/functional';
import GroupsGlance from './GroupsGlance';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { BrandSegment, HourFormat } from '@go-shared/types/brand';
import Fuse from 'fuse.js';
import { EvaluatedEventTemplate, EventTemplate } from '@go-shared/types/eventTemplate';
import EventsGlance from './EventsGlance';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';

export interface EventListPanelProps {
  open: boolean;
  width: number;
  locked: boolean;
  levelLabel: string;
  events: Event[];
  groupEvents: GroupEvent[];
  dayStart: number;
  hourFormat: HourFormat;
  onAddIconClick: () => void;
  onToggleEventHideClick: (event: Event) => void;
  onEditEventClick: (event: Event) => void;
  onDeleteEventClick: (eventId: string) => void;
  onSetShowEventListPanelClick: (show: boolean) => void;
  onStaffAllocationEditClick?: (event: Event) => void;
  onUpdateEvent?: (event: Event) => void;
}

/**
 * Minimum attributes to show an event summary on the list panel
 *
 */
export type EventSummaryType = 'event' | 'groupEvent';

export interface EventSummaryBase {
  _id: string;
  startTime: Time;
  duration: number;
  title: string;
  venueName: string;
  venueLevel: number;
  hidden: boolean;
}

export interface EventSummaryEvent {
  type: 'event';
  _template: EventTemplate;
  _event: Event;
}

interface EventSummaryGroupBooking {
  type: 'groupEvent';
  _bookingId: string;
  _groupId: string;
}

type EventSummary = EventSummaryBase & (EventSummaryEvent | EventSummaryGroupBooking);

/**
 * Similar to `makeEventSorter`, but works for Events and GroupEvents (which have been
 * converted to `EventSummary`)
 */
export function makeSummarySorter(dayStart: number) {
  return (e1: EventSummary, e2: EventSummary) => {
    let startTime1 = timeToMinutes(e1.startTime, dayStart);
    let startTime2 = timeToMinutes(e2.startTime, dayStart);

    let duration1 = e1.duration;
    let duration2 = e2.duration;

    if (startTime1 !== startTime2) return startTime1 - startTime2;
    if (duration1 !== duration2) return duration1 - duration2;

    return e1.title.localeCompare(e2.title);
  };
}

function formatEventTime(e: EventSummary, hourFormat: HourFormat): string {
  let startTime = timeToString(e.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT);
  let endTime = timeToString(
    addMinutesToTime(e.startTime, e.duration),
    hourFormat,
    DEFAULT_TIMESTR_FORMAT
  );

  return `${startTime} – ${endTime}`;
}

function formatEventTitle(e: EventSummary): string {
  return `${e.title}`;
}

export function formatEventVenue(
  venueName: string,
  venueLevel: number,
  levelLabel: string
): string {
  return `${venueName}, ${levelLabel} ${venueLevel}`;
}

function findEvent(eventId: string, events: Event[]): Event {
  // We can assume there's a result, since the id comes from the list of events
  return events.find(e => e._id === eventId)!;
}

interface EventSummaryItem {
  id: string;
  title: string;
  time: string;
  venue: string;
  hidden: boolean;
  expanded: boolean;
  type: EventSummaryType;
  _summary: EventSummary;
}

function EventListPanel(props: EventListPanelProps) {
  const {
    open,
    width,
    locked,
    levelLabel,
    events,
    groupEvents,
    dayStart,
    hourFormat,
    onAddIconClick,
    onToggleEventHideClick,
    onEditEventClick,
    onDeleteEventClick,
    onSetShowEventListPanelClick,
    onUpdateEvent,
  } = props;

  const { user } = useUserInfo();
  const { activeProperty } = useActiveProperty();
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const [expandedReqId, setExpandedReqId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  let eventSorter = makeSummarySorter(dayStart);
  let eventSummaries = useMemo(
    () =>
      events
        .map<EventSummary>(e => ({
          _id: e._id,
          type: 'event',
          startTime: e.startTime,
          duration: e.duration,
          title: (e.template as EvaluatedEventTemplate)._evaluatedTitle || e.template.title,
          venueName: e.venue.name,
          venueLevel: e.venue.level,
          hidden: e.hidden,
          _template: e.template,
          _event: e,
        }))
        .concat(
          groupEvents.map<EventSummary>(e => {
            return {
              _id: e._id,
              _bookingId: e.groupRequest,
              _groupId: e.groupBooking,
              startTime: e.startTime,
              duration: e.duration,
              title: `${e.group} - ${e.title}`,
              venueName: e.venue.name,
              venueLevel: e.venue.level,
              hidden: false,
              type: 'groupEvent',
            };
          })
        ),
    [events, groupEvents]
  );

  eventSummaries.sort(eventSorter);

  const tableItems: EventSummaryItem[] = useMemo(
    () =>
      eventSummaries.map(e => ({
        id: e._id,
        title: formatEventTitle(e),
        time: formatEventTime(e, hourFormat),
        venue: formatEventVenue(e.venueName, e.venueLevel, levelLabel),
        hidden: e.hidden,
        expanded: e._id === expandedReqId,
        type: e.type,
        _summary: e,
      })),
    [eventSummaries, expandedReqId]
  );

  const tableFields: Array<BubbleTableFields<EventSummaryItem>> = [
    {
      key: 'time',
    },
    {
      key: 'status',
      label: '',
    },
    {
      key: 'title',
    },
    {
      key: 'actions',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-0-i text-right' },
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<EventSummaryItem>> = [
    {
      key: 'time',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        const [startTime, endTime] = item.time.split(' – ');
        return (
          <>
            <div className="whitespace-nowrap font-semibold">{startTime}</div>
            <div>{endTime}</div>
          </>
        );
      },
    },
    {
      key: 'status',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        switch (item._summary.type) {
          case 'groupEvent':
            return (
              <div className="text-center">
                <InlineIcon name="group" />
              </div>
            );
          default:
            return (
              <div className="whitespace-nowrap flex justify-center -mr-1 not-italic">
                {item._summary._template.simple && (
                  <BubbleBadge
                    variant="ocean"
                    circle
                    style={{ width: 20, height: 20 }}
                    className="p-0-i grid items-center justify-center mr-1 text-center"
                  >
                    S
                  </BubbleBadge>
                )}
                {item._summary._template.inventoried && (
                  <BubbleBadge
                    variant="grape"
                    circle
                    style={{ width: 20, height: 20 }}
                    className="p-0-i grid items-center justify-center mr-1 text-center"
                  >
                    I
                  </BubbleBadge>
                )}
              </div>
            );
        }
      },
    },
    {
      key: 'title',
      tdProps: makeDefaultTemplateTdProps,
      render: item => (
        <>
          <div className="font-semibold">{item.title}</div>
          <div>{item.venue}</div>
        </>
      ),
    },
    {
      key: 'actions',
      tdProps: makeDefaultTemplateTdProps,
      render: item => (
        <div className="text-right">
          {item.type !== 'groupEvent' && !locked && (
            <>
              {!showDelete[item.id] && (
                <BubbleButton
                  className="p-2-i"
                  variant="crimson"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [item.id]: true });
                  }}
                  icon="trash-alt"
                />
              )}
              {showDelete[item.id] && (
                <>
                  <BubbleButton
                    className="p-2-i"
                    variant="crimson"
                    inverse
                    onClick={() => onDeleteEventClick(findEvent(item._summary._id, events)._id)}
                    icon="trash-check"
                  />
                  <BubbleButton
                    className="p-2-i"
                    variant="slate"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [item.id]: false });
                    }}
                    icon="trash-xmark"
                  />
                </>
              )}
              <BubbleButton
                className="p-2-i"
                variant="slate"
                inverse
                onClick={() => onEditEventClick(findEvent(item._summary._id, events))}
                icon="edit"
              />
              <BubbleButton
                className="p-2-i"
                variant={item.hidden ? 'smoke' : 'navy'}
                inverse
                onClick={() => onToggleEventHideClick(findEvent(item._summary._id, events))}
                icon={item.hidden ? 'eye-slash' : 'eye'}
              />
            </>
          )}
          <BubbleButton
            className="p-2-i mr-3"
            variant={item.type === 'groupEvent' ? 'white' : 'navy'}
            inverse
            onClick={() => setExpandedReqId(expandedReqId === item.id ? undefined : item.id)}
            icon={
              expandedReqId === item.id
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }
          />
        </div>
      ),
    },
  ];

  function makeDefaultTemplateTdProps(
    data: EventSummaryItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { expanded, hidden, type } = data;
    let segment: BrandSegment | undefined;

    if (data._summary.type === 'event') {
      segment = data._summary._template.segment;
    }

    return {
      ...fieldTdProps,
      style: {
        backgroundColor:
          type !== 'groupEvent' && !expanded && segment ? `${segment.color}1A` : undefined,
      },
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        type === 'groupEvent' ? 'bg-forest text-white' : undefined,
        type !== 'groupEvent' && expanded ? 'bg-bubblegum-lightest' : undefined,
        hidden ? 'italic text-slate-lighter' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  function tableDetailsRender(item: EventSummaryItem) {
    switch (item._summary.type) {
      case 'groupEvent':
        return (
          <div className="py-3">
            <GroupsGlance
              user={user}
              hideComments
              hideStatusChange
              hideVenueDayView
              property={activeProperty}
              groupId={item._summary._groupId}
              bookingId={item._summary._bookingId}
            />
          </div>
        );
      default:
        return (
          <div className="pb-3 pt-2">
            <div className="bg-white border shadow px-4 py-3">
              <EventsGlance event={item._summary._event} onUpdateEvent={onUpdateEvent} />
            </div>
          </div>
        );
    }
  }

  const fuse = useMemo(() => {
    return new Fuse(tableItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['title', 'venue'],
    });
  }, [tableItems]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredTableItems = fuseSearchResults || tableItems;

  const togglePanelBtn = (
    <BubbleButton
      variant={open ? 'white' : 'ocean'}
      inverse={open}
      className="h-full py-0-i rounded-0-i grid items-center justify-center"
      onClick={() => onSetShowEventListPanelClick(!open)}
      icon={open ? 'xmark-large' : 'list'}
    />
  );

  const addEvtBtn = (
    <BubbleButton
      className="h-full py-0-i rounded-0-i grid items-center justify-center"
      variant="lime"
      onClick={onAddIconClick}
      icon="plus"
    />
  );

  return (
    <div
      className={`EventListPanel absolute top-0 right-0 border-l w-full flex flex-col bg-white ${
        open ? 'open' : ''
      }`}
      style={{ width }}
    >
      <div className="EventListPanelList flex-shrink-0 flex items-stretch bg-slate">
        {(!open || !locked) && <div>{open ? addEvtBtn : togglePanelBtn}</div>}
        <span className="mx-3 font-bold uppercase text-white self-center">DAILY SCHEDULE</span>
        <div className="ml-auto"></div>
        <div className="self-center text-white font-bold">
          {`${tableItems.filter(t => !t.hidden && t.type !== 'groupEvent').length} Visible, ${
            tableItems.filter(t => t.hidden && t.type !== 'groupEvent').length
          } Hidden`}
        </div>
        <div>{open ? togglePanelBtn : addEvtBtn}</div>
      </div>
      <div className="flex-grow overflow-y-auto">
        <div className="flex items-center sticky top-0 bg-white shadow">
          <BubbleInput
            type="text"
            appearance="bare"
            className="flex-grow shadow-0-i px-3 py-2 bg-white"
            inputClassName="rounded-0-i bg-white"
            placeholder="Search..."
            block
            value={search}
            onValue={setSearch}
          />
          <BubbleButton
            variant="navy"
            inverse
            className="rounded-0-i"
            onClick={() => setSearch('')}
            icon="eraser"
          />
        </div>
        <BubbleTable
          slim
          responsive
          hideHeader
          headVariant="navy"
          className="shadow-none-i"
          keyExtractor={e => e.id}
          stickyHeader={0}
          items={filteredTableItems}
          fields={tableFields}
          templates={tableTemplates}
          detailsKey={expandedReqId}
          detailsRender={tableDetailsRender}
          detailsClassName="bg-bubblegum-lightest"
        />
      </div>
    </div>
  );
}

export default EventListPanel;
